import { useCallback, useMemo, useState } from 'react';

import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';

import {
  Alert,
  BasicTableStyled,
  Box,
  Button,
  GridColDef,
  GridLogicOperator,
  GridPaginationModel,
  GridProSlotProps,
  GridToolbarContainer,
  GridToolbarFilterButton,
  Stack,
  Typography,
  useGridSelection,
} from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import { useAdminFacilitiesPreProcessOnboardingGet } from '../../../services/admin-facility';
import useColumns from './use-columns';
import { useFiltersModel } from './use-filters-model';

interface SbvProDatGridProps {
  databaseName: string;
}

const slotProps: GridProSlotProps = {
  filterPanel: {
    logicOperators: [GridLogicOperator.And],
  },
};

const CustomToolbar =
  ({
    isRefreshing,
    onRefresh,
  }: {
    isRefreshing: boolean;
    onRefresh: () => void;
  }) =>
  () => {
    return (
      <Box display="flex" justifyContent="space-between" p={1}>
        <GridToolbarContainer>
          <GridToolbarFilterButton />
        </GridToolbarContainer>
        <Button
          size="small"
          disabled={isRefreshing}
          onClick={onRefresh}
          variant="contained"
        >
          {isRefreshing ? '...Refreshing' : 'Refresh'}
        </Button>
      </Box>
    );
  };

const getRowId = (item: GridValidRowModel) => item.pkEmployeeId as number;

const EmployeeDuplicateUsersGrid = ({ databaseName }: SbvProDatGridProps) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 1,
    pageSize: 25,
  });

  const { filterModel, setFilterModel, apiFilters } = useFiltersModel();

  const columns = useColumns();
  const { ids, rowSelectionModel, onRowSelectionModelChange } =
    useGridSelection();
  const { data, isLoading, isRefetching, refetch, isError, error } =
    useAdminFacilitiesPreProcessOnboardingGet({
      databaseName: databaseName,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      ...apiFilters,
    });

  const employees = data?.data;
  const rowCount = data?.rowCount ?? 0;
  const handleClickOnBoard = useCallback(() => {
    const selectedEmployees = filter(employees, (item) =>
      ids.includes(item.pkEmployeeId as number)
    );
  }, [employees, ids]);

  const onRefresh = useCallback(async () => {
    await refetch().catch(noop);
  }, [refetch]);

  const noRowsOverlay = useMemo(() => {
    return !isError
      ? () => (
          <Stack height={'85%'} justifyContent={'center'} alignItems={'center'}>
            <Typography>No data found!</Typography>
          </Stack>
        )
      : () => (
          <Stack height={'85%'} justifyContent={'center'} alignItems={'center'}>
            <Alert color={'error'} variant={'outlined'}>
              <Typography>{getApiErrorMessage(error)}</Typography>
            </Alert>
          </Stack>
        );
  }, [error, isError]);
  console.log(employees?.length);
  return (
    <>
      <Box sx={{ overflow: 'hidden', height: '100%' }}>
        <BasicTableStyled
          name="facility-users-grid"
          rows={employees ?? []}
          columns={columns as GridColDef[]}
          getRowId={getRowId}
          filterMode="server"
          paginationMode="server"
          checkboxSelection
          pagination
          pageSizeOptions={[25, 50, 75, 100]}
          hideFooter={isLoading}
          rowCount={rowCount}
          initialState={{
            pagination: { paginationModel },
            pinnedColumns: { right: ['actions'] },
          }}
          loading={isLoading}
          slotProps={slotProps}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          onPaginationModelChange={setPaginationModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          filterDebounceMs={1000}
          slots={{
            toolbar: CustomToolbar({ isRefreshing: isRefetching, onRefresh }),
            noRowsOverlay,
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer':
              {
                visibility: 'hidden',
              },
          }}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" columnGap={2}>
        <Button
          disabled={isEmpty(rowSelectionModel)}
          variant="contained"
          onClick={handleClickOnBoard}
        >
          Process
        </Button>
      </Box>
    </>
  );
};

export default EmployeeDuplicateUsersGrid;
